import { isOwnerBoundKey, LegacyMediaKeyError } from '@trilirivos/media-keys';
import { apolloClient } from '../api/apolloClient';
import { DELETE_POST_MEDIA } from '../graphql/resolvers/helperResolvers/deletePostMedia';

// Re-exported so existing importers keep working; the identity is the SHARED one (TRIL-628).
export { LegacyMediaKeyError };

/**
 * TRIL-615: delete an uploaded image from the POST_MEDIA bucket.
 *
 * WHAT THIS REPLACED, because the old shape is the whole bug. This used to `fetch` a DELETE against
 * `import.meta.env.VITE_API_POST_MEDIA` with a HARDCODED API Gateway fallback. `.env.demo` never
 * set that variable, so every demo build used the hardcoded URL; that endpoint answered 403; and no
 * credentials were ever sent (the source carried "TODO: do I need authorization headers here?").
 * Every caller then swallowed the rejection into a `console.error`, so the block disappeared from
 * the UI and the object stayed in a PUBLICLY READABLE bucket. A user who uploaded the wrong image,
 * removed it and saved had not actually withdrawn it.
 *
 * Going through GraphQL means it inherits auth, endpoint configuration and the error contract that
 * everything else already has, instead of being a second surface that had to be configured
 * correctly and never was. Nothing about the signature changed, so all nine call sites are
 * unaffected.
 *
 * IT STILL THROWS ON FAILURE, and callers must keep letting it. Silently treating a failed delete
 * as success is what made this invisible for so long.
 */
export const deleteImageFromS3 = async (
  imageKey: string,
): Promise<{ key: string }> => {
  if (!imageKey) {
    throw new Error('deleteImageFromS3: no key supplied');
  }

  // Objects uploaded BEFORE keys were owner-bound (TRIL-617) sit at the bucket root as
  // `<uuid>.<ext>` with nothing identifying an owner, so no caller can prove the right to delete
  // one and the server refuses them — correctly, and permanently. Fail fast with a DISTINCT error
  // rather than sending a request guaranteed to come back FORBIDDEN, because the difference matters
  // to the user: a transient failure is worth retrying and this is not. Telling somebody to "try
  // again" when no retry can ever succeed, by anyone, on any surface, is its own small dishonesty —
  // and this ticket exists because the product lied about whether an image had been removed.
  //
  // Cleaning up the existing legacy objects is a server-side job (they are unreachable from here by
  // design) — TRIL-625.
  if (!isOwnerBoundKey(imageKey)) {
    throw new LegacyMediaKeyError(imageKey);
  }

  const { data } = await apolloClient.mutate({
    mutation: DELETE_POST_MEDIA,
    variables: { key: imageKey },
  });

  // The field is Boolean! so `false` cannot come back without an error — but a null `data` can, if
  // the mutation errored under an errorPolicy that does not throw. Treat anything but an explicit
  // true as a failure rather than assuming success.
  if (data?.deletePostMedia !== true) {
    throw new Error(`Failed to delete image: ${imageKey}`);
  }

  return { key: imageKey };
};
